import React, { useEffect } from 'react';

import {
	SpotlightManager,
	SpotlightPulse,
	SpotlightTarget,
	SpotlightTransition,
} from '@atlaskit/onboarding';
import { type Actions } from '@atlaskit/onboarding/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';

import { useChoreographerAssets } from '@confluence/choreographer-services';

import type { PresenceContentMode, PresenceContentType } from '../presenceTypes';

import {
	PRESENCE_AVATAR_GROUP_ANALYTIC_SOURCE,
	usePresenceChangeboarding,
} from './usePresenceChangeboarding';
import { useTelepointerChangeboarding } from './useTelepointerChangeboarding';

export type PresenceChangeboardingProps = {
	children: React.ReactNode;
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	totalParticipants: number;
};

export type PresenceChangeboardingFeatureProps = Partial<
	Omit<PresenceChangeboardingProps, 'children'>
> & {
	skip?: boolean;
};

export enum ChangeboardingState {
	INITIAL, // Initial start and loading are combined in this state. No spotlight render.
	ERROR, // If get or set onboarding state calls error out. No spotlight render.
	READY, // Ready to show the pulsing. UI renders spotlights.
	ACTIVE, // Interacted with pulse and ready to show the spotlight. UI renders spotlights.
	COMPLETED, // Interacted with spotlight. No spotlight render.
}

export type PresenceChangeboardingFeatureResponse = {
	featureName: string;
	state: ChangeboardingState;
	content: {
		image: string;
		actions: Actions;
		heading: string;
		label: string;
		description: string;
		onPulseClick: () => void;
		onBlanketClick?: () => void;
	};
};

// The pulse component is wrapping avatar group which has buttons and click events.
// To have the pulse component take precedence, we need to temporarily disable the elements inside.
const disablePointerEventsStyle = xcss({ pointerEvents: 'none' });
const cursorStyle = xcss({ cursor: 'pointer' });
const PointerEvents = ({
	children,
	shouldDisable,
}: {
	children: React.ReactNode;
	shouldDisable: boolean;
}) => {
	if (shouldDisable) {
		return (
			<Box xcss={cursorStyle}>
				<Box xcss={disablePointerEventsStyle}>{children}</Box>
			</Box>
		);
	}

	return <>{children}</>;
};

export const PresenceChangeboarding = ({
	children,
	contentId,
	contentMode,
	contentType,
	totalParticipants,
}: PresenceChangeboardingProps) => {
	const { Spotlight } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const presenceChangeboarding = usePresenceChangeboarding({
		contentId,
		contentMode,
		contentType,
		totalParticipants,
	});
	const telepointerChangeboarding = useTelepointerChangeboarding({
		contentId,
		contentMode,
		contentType,
		// Only execute the telepointer changeboarding if the user has completed the first one
		skip: presenceChangeboarding.state !== ChangeboardingState.COMPLETED,
	});

	let data: PresenceChangeboardingFeatureResponse | null = null;

	// After this point, we won't handle any other states than READY or ACTIVE
	if (
		presenceChangeboarding.state === ChangeboardingState.ACTIVE ||
		presenceChangeboarding.state === ChangeboardingState.READY
	) {
		data = presenceChangeboarding;
	} else if (
		telepointerChangeboarding.state === ChangeboardingState.ACTIVE ||
		telepointerChangeboarding.state === ChangeboardingState.READY
	) {
		data = telepointerChangeboarding;
	}

	useEffect(() => {
		if (data?.state === ChangeboardingState.READY) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					// TODO: This must be the dynamic source
					// https://product-fabric.atlassian.net/browse/MDS-1330
					source: PRESENCE_AVATAR_GROUP_ANALYTIC_SOURCE,
					actionSubject: 'PresenceAvatarGroup',
					action: 'pulse shown',
					attributes: {
						contentId,
						contentMode,
						contentType,
						changeboardingFeature: data.featureName,
					},
				},
			}).fire();
		}

		if (data?.state === ChangeboardingState.ACTIVE) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: PRESENCE_AVATAR_GROUP_ANALYTIC_SOURCE,
					actionSubject: 'PresenceAvatarGroup',
					action: 'spotlight shown',
					attributes: {
						contentId,
						contentMode,
						contentType,
						changeboardingFeature: data.featureName,
					},
				},
			}).fire();
		}
	}, [data, contentId, contentMode, contentType, createAnalyticsEvent]);

	if (data === null) {
		return <>{children}</>;
	}

	return (
		<SpotlightManager blanketIsTinted={false} onBlanketClicked={data.content.onBlanketClick}>
			<SpotlightTarget name="presence-avatar">
				<SpotlightPulse
					testId="presence-pulse"
					data-vc="presence-avatar-group-container"
					pulse={data?.state === ChangeboardingState.READY}
					radius={3}
					onClick={data.content.onPulseClick}
				>
					<PointerEvents shouldDisable={data?.state === ChangeboardingState.READY}>
						{children}
					</PointerEvents>
				</SpotlightPulse>
			</SpotlightTarget>

			<SpotlightTransition>
				{data?.state === ChangeboardingState.ACTIVE && (
					<Spotlight
						target="presence-avatar"
						key="presence-avatar-spotlight"
						testId="presence-avatar-spotlight"
						image={data.content?.image}
						actions={data.content?.actions}
						heading={data.content?.heading}
						label={data.content?.label}
						dialogPlacement="bottom right"
						targetRadius={3}
						shouldWatchTarget
					>
						{data.content?.description}
					</Spotlight>
				)}
			</SpotlightTransition>
		</SpotlightManager>
	);
};
